import React, { createContext, useContext, useState, useEffect } from 'react'
import { collection, query, getDocs, orderBy } from 'firebase/firestore'
import { db, getCollectionName } from '../firebase'

const DataContext = createContext()

export const useData = () => {
    const context = useContext(DataContext)
    if (!context) {
        throw new Error('useData must be used within a DataProvider')
    }
    return context
}

export const DataProvider = ({ children }) => {
    const [inventory, setInventory] = useState([])
    const [flights, setFlights] = useState([])
    const [airports, setAirports] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        const loadData = async () => {
            try {
                // Load flights
                const flightsQuery = query(collection(db, getCollectionName('flights')))
                const flightsSnapshot = await getDocs(flightsQuery)
                const flightsData = flightsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                setFlights(flightsData)

                // Load inventory
                const inventoryQuery = query(
                    collection(db, getCollectionName('inventory')),
                    orderBy('departureDate', 'asc')
                )
                const inventorySnapshot = await getDocs(inventoryQuery)

                const formattedInventoryData = inventorySnapshot.docs.map(doc => {
                    const data = doc.data()
                    return {
                        id: doc.id,
                        departure: flightsData.find(flight => flight.id === data.flightNo).departure,
                        arrival: flightsData.find(flight => flight.id === data.flightNo).arrival,
                        ...data,
                    }
                })
                setInventory(formattedInventoryData)

                // Load airports
                const airportsQuery = query(collection(db, getCollectionName('airports')))
                const airportsSnapshot = await getDocs(airportsQuery)
                const airportsData = airportsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                setAirports(airportsData)

                setError(null)
            } catch (err) {
                console.error('Error loading data:', err)
                setError('Failed to load data')
            } finally {
                setLoading(false)
            }
        }

        loadData()
    }, [])

    const refreshData = async () => {
        setLoading(true)
        try {
            // Refresh flights
            const flightsQuery = query(collection(db, getCollectionName('flights')))
            const flightsSnapshot = await getDocs(flightsQuery)
            const flightsData = flightsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }))
            setFlights(flightsData)

            // Refresh inventory
            const inventoryQuery = query(
                collection(db, getCollectionName('inventory')),
                // where('departureDate', '>=', today),
                orderBy('departureDate', 'asc')
            )
            const inventorySnapshot = await getDocs(inventoryQuery)
            const inventoryData = inventorySnapshot.docs.map(doc => {
                const data = doc.data()
                return {
                    id: doc.id,
                    departure: flightsData.find(flight => flight.id === data.flightNo).departure,
                    arrival: flightsData.find(flight => flight.id === data.flightNo).arrival,
                    ...doc.data()
                }
            })
            setInventory(inventoryData)

            // Refresh airports
            const airportsQuery = query(collection(db, getCollectionName('airports')))
            const airportsSnapshot = await getDocs(airportsQuery)
            const airportsData = airportsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }))
            setAirports(airportsData)

            setError(null)
        } catch (err) {
            console.error('Error refreshing data:', err)
            setError('Failed to refresh data')
        } finally {
            setLoading(false)
        }
    }

    return (
        <DataContext.Provider value={{
            flights,
            inventory,
            airports,
            loading,
            error,
            refreshData
        }}>
            {children}
        </DataContext.Provider>
    )
} 